import React from "react";
import { Alert, HStack, Typography, VStack } from "../primitives";
import {
  CROSS_REFERENCE_ATTRIBUTES,
  areAttributeValuesEqual,
  getExistingSameFiBillerId,
  getOtherFiKeys,
  hasBillerId,
} from "../../constants/bcrReview.constants";
import BcrReviewFieldRow from "./BcrReviewFieldRow";

const BcrReviewMatchSection = ({
  t,
  link,
  bcrDto,
  pendingBcr,
  incomingFiKey,
  incomingFiLabel,
  notAvailableLabel,
  hideDuplicateWarning,
  hideHeader = false,
}) => {
  const existingSameFiBillerId = getExistingSameFiBillerId(bcrDto, incomingFiKey);
  const isPotentialDuplicate = hasBillerId(existingSameFiBillerId);

  return (
      <VStack spacing={2} className={hideHeader ? undefined : "bcr-review__section"}>
      {!hideHeader && (
        <Typography variant="body1" className="bcr-review__similarity">
          <strong>{link.targetCentralizedBillerId}</strong>
          <span className="bcr-review__similarity-value">
            {t("erps.billermanagement.bcrreview.namesimilarity", "Name similarity: {{value}}%", {
              value: link.nameSimilarity,
            })}
          </span>
          <strong>{t(`erps.billermanagement.bcrreview.matchtype.${link.matchType}`)}</strong>
        </Typography>
      )}
      {isPotentialDuplicate && !hideDuplicateWarning && (
        <Alert severity="warning" className="bcr-review__duplicate-warning">
          <Typography variant="body1" component="p" className="bcr-review__duplicate-warning-title">
            {t("erps.billermanagement.bcrreview.duplicate.title", "Potential duplicate biller.")}
          </Typography>
          <Typography variant="body2" component="p">
            {t(
              "erps.billermanagement.bcrreview.duplicate.message",
              "{{centralizedBillerId}} already has a {{fi}} biller linked ({{existingBillerId}}). Verify whether the incoming biller is the same biller. Approving this link will put two same-FI billers on one centralized id. Rejecting this link will create a new centralized id for this incoming biller.",
              {
                centralizedBillerId: link.targetCentralizedBillerId,
                fi: incomingFiLabel,
                existingBillerId: existingSameFiBillerId,
              }
            )}
          </Typography>
        </Alert>
      )}
      <BcrReviewFiComparisonGrid
        t={t}
        bcrDto={bcrDto}
        pendingBcr={pendingBcr}
        incomingFiKey={incomingFiKey}
        incomingFiLabel={incomingFiLabel}
        notAvailableLabel={notAvailableLabel}
      />
    </VStack>
  );
};

const BcrReviewFiComparisonGrid = ({
  t,
  bcrDto,
  pendingBcr,
  incomingFiKey,
  incomingFiLabel,
  notAvailableLabel,
}) => (
  <HStack
    className="bcr-review__columns"
    spacing={2}
    alignItems="stretch"
    useFlexGap
    role="region"
    aria-label={t("erps.billermanagement.bcrreview.comparison.aria", "Biller cross reference comparison")}
  >
    <BcrReviewIncomingColumn
      t={t}
      pendingBcr={pendingBcr}
      incomingFiKey={incomingFiKey}
      incomingFiLabel={incomingFiLabel}
      notAvailableLabel={notAvailableLabel}
    />
    {getOtherFiKeys(incomingFiKey).map((fiKey) => (
      <BcrReviewCrossReferenceColumn
        key={fiKey}
        t={t}
        fiKey={fiKey}
        fiLabel={t(`erps.billermanagement.crossreference.label.${fiKey}BillerId`)}
        bcrDto={bcrDto}
        incomingProfile={pendingBcr?.incomingBillerProfileDto}
        notAvailableLabel={notAvailableLabel}
      />
    ))}
  </HStack>
);

const BcrReviewIncomingColumn = ({ t, pendingBcr, incomingFiKey, incomingFiLabel, notAvailableLabel }) => {
  const incomingProfile = pendingBcr?.incomingBillerProfileDto;
  const fiLabel = incomingFiKey
    ? t(`erps.billermanagement.crossreference.label.${incomingFiKey}BillerId`)
    : incomingFiLabel;

  return (
    <VStack spacing={0} className="bcr-review__fi-card">
      <Typography variant="h3" className="bcr-review__fi-card-header">
        <strong>{fiLabel}: {pendingBcr?.incomingBillerId}</strong>
      </Typography>
      <VStack className="bcr-review__field-list">
        <BcrReviewFieldRow
          label={t("erps.billermanagement.label.billerName")}
          value={incomingProfile?.billerName}
          notAvailableLabel={notAvailableLabel}
        />
        {CROSS_REFERENCE_ATTRIBUTES.map((attr) => (
          <BcrReviewFieldRow
            key={attr.incomingField}
            label={t(attr.labelKey)}
            value={incomingProfile?.[attr.incomingField]}
            notAvailableLabel={notAvailableLabel}
          />
        ))}
      </VStack>
    </VStack>
  );
};

const BcrReviewCrossReferenceColumn = ({ t, fiKey, fiLabel, bcrDto, incomingProfile, notAvailableLabel }) => {
  const billerId = bcrDto?.[`${fiKey}BillerId`];
  const headingId = hasBillerId(billerId) ? billerId : notAvailableLabel;
  const matchLabel = t("erps.billermanagement.bcrreview.badge.match", "match");
  const differsLabel = t("erps.billermanagement.bcrreview.badge.differs", "differs");

  return (
    <VStack spacing={0} className="bcr-review__fi-card">
      <Typography variant="h4" className="bcr-review__fi-card-header">
        <strong>{fiLabel}: {headingId}</strong>
      </Typography>
      {hasBillerId(billerId) && (
        <VStack className="bcr-review__field-list">
          <BcrReviewFieldRow
            label={t("erps.billermanagement.label.billerName")}
            value={bcrDto?.[`${fiKey}BillerName`]}
            notAvailableLabel={notAvailableLabel}
          />
          {CROSS_REFERENCE_ATTRIBUTES.map((attr) => {
            const similarityType = areAttributeValuesEqual(
              incomingProfile?.[attr.incomingField],
              bcrDto?.[`${fiKey}${attr.suffix}`]
            )
              ? "match"
              : "differs";
            return (
              <BcrReviewFieldRow
                key={attr.suffix}
                label={t(attr.labelKey)}
                value={bcrDto?.[`${fiKey}${attr.suffix}`]}
                notAvailableLabel={notAvailableLabel}
                similarityType={similarityType}
                badgeLabel={similarityType === "match" ? matchLabel : differsLabel}
              />
            );
          })}
        </VStack>
      )}
    </VStack>
  );
};

export default BcrReviewMatchSection;
